import type { ChangeEvent } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Center,
  CloseButton,
  Container,
  Group,
  Loader,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Title
} from '@mantine/core';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import type { Contract, WeekReport } from '@/api/generated/models';
import { useListContracts } from '@/api/generated/contracts/contracts';
import { listTeams, useListDepartments } from '@/api/generated/departments-teams/departments-teams';
import { useListUsers } from '@/api/generated/users/users';
import {
  getListAttachmentsQueryKey,
  useDeleteAttachment,
  useGetWeekReport,
  useListAttachments,
  useListHours,
  useListWeekReportLines,
  useSubmit,
  useUploadAttachment
} from '@/api/generated/week-reports/week-reports';
import { getPlannedItem } from '@/api/generated/work-structure/work-structure';
import {
  IconFigmaAlertTriangle,
  IconFigmaArrowRight,
  IconFigmaLock,
  IconFigmaTrash
} from '@/assets/icons';
import {
  CardSection,
  FormActions,
  MutationButton,
  SelectField,
  StatusPill,
  tableRowStyle
} from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { SubmitSuccessModal } from '@/features/week-reports/components/SubmitSuccessModal';
import { formatWeekLabel, formatWeekPeriod } from '@/features/week-reports/weekReportDisplay';
import { otherPartyName } from '@/utils/format';

export const WeekReportCorrectionPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [submitSuccessOpen, setSubmitSuccessOpen] = useState(false);
  const [uploadVisibleToClient, setUploadVisibleToClient] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useGetWeekReport(id ?? '');
  const report = data as WeekReport | undefined;

  const { data: linesData } = useListWeekReportLines(id ?? '');
  const lines = linesData ?? [];

  const plannedItemIds = useMemo(
    () => [...new Set(lines.map((line) => line.plannedItemId).filter((v): v is string => !!v))],
    [lines]
  );
  const plannedItemQueries = useQueries({
    queries: plannedItemIds.map((piId) => ({
      queryKey: ['planned-item', piId],
      queryFn: () => getPlannedItem(piId),
      enabled: !!piId
    }))
  });
  const plannedItemMap = new Map(
    plannedItemIds.map((piId, i) => [
      piId,
      plannedItemQueries[i]?.data as { plannedQty?: number } | undefined
    ])
  );

  const { data: hoursData } = useListHours(id ?? '');
  const hours = hoursData ?? [];

  const { data: attachmentsData } = useListAttachments(id ?? '');
  const attachments = attachmentsData ?? [];

  const { data: contractsData } = useListContracts();
  const contracts: Contract[] = contractsData ?? [];
  const contract = contracts.find((c) => c.id === report?.contractId);

  const { data: usersData } = useListUsers();
  const userMap = new Map(
    (usersData ?? []).map((u) => [u.id, `${u.firstName} ${u.lastName}`.trim()])
  );

  const { data: departmentsData } = useListDepartments();
  const departments = departmentsData ?? [];
  const teamQueries = useQueries({
    queries: departments.map((dept) => ({
      queryKey: ['teams', dept.id],
      queryFn: () => listTeams(dept.id ?? ''),
      enabled: !!dept.id
    }))
  });
  const teamName = useMemo(
    () => teamQueries.flatMap((q) => q.data ?? []).find((team) => team.id === report?.teamId)?.name,
    [teamQueries, report?.teamId]
  );

  const submitMutation = useSubmit({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['/api/v1/week-reports'] });
        setSubmitSuccessOpen(true);
      }
    }
  });

  const invalidateAttachments = () =>
    queryClient.invalidateQueries({ queryKey: getListAttachmentsQueryKey(id) });
  const uploadAttachmentMutation = useUploadAttachment({
    mutation: { onSuccess: invalidateAttachments }
  });
  const deleteAttachmentMutation = useDeleteAttachment({
    mutation: { onSuccess: invalidateAttachments }
  });

  const handleFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file || !id) return;
    uploadAttachmentMutation.mutate({
      id,
      data: { file },
      params: { isVisibleToClient: uploadVisibleToClient }
    });
  };

  if (isLoading || !report) {
    return (
      <Container size="lg" px={0}>
        <Center py={80}>
          <Loader size="sm" />
        </Center>
      </Container>
    );
  }

  const itemColumns: [string, number][] = [
    ['newWeekReport.colItem', 210],
    ['newWeekReport.colSource', 130],
    ['newWeekReport.colRealizedQty', 130],
    ['newWeekReport.colUnit', 80],
    ['newWeekReport.colPrice', 150],
    ['newWeekReport.colNote', 190]
  ];

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <Stack gap={8}>
          <Group gap={8} wrap="wrap">
            <Title
              order={2}
              fz={{ base: 22, sm: 24 }}
              style={{ color: 'var(--color-text-primary)' }}
            >
              {t('weekReportCorrection.title', { week: report.weekNumber })}
            </Title>
            <StatusPill
              label={t('weekReportCorrection.badge')}
              bg="var(--color-bg-surface-2)"
              fg="var(--color-text-secondary)"
            />
          </Group>
          <Text fz={14} style={{ color: 'var(--color-text-secondary)' }}>
            {t('weekReportCorrection.subtitle')}
          </Text>
        </Stack>

        {report.rejectionReason && !bannerDismissed && (
          <Group
            gap={10}
            p={20}
            align="flex-start"
            wrap="nowrap"
            style={{
              width: '100%',
              borderRadius: 8,
              border: '1px solid var(--color-status-warning)',
              backgroundColor: 'var(--color-status-warning-soft)',
              color: 'var(--color-status-warning)'
            }}
          >
            <IconFigmaAlertTriangle size={16} />
            <Stack gap={8} style={{ flex: 1 }}>
              <Text fz={13} fw={600} style={{ color: 'inherit' }}>
                {t('weekReportCorrection.bannerTitle')}
              </Text>
              <Text fz={13} style={{ color: 'inherit' }}>
                {t('weekReportCorrection.bannerBody')}
              </Text>
              <Stack gap={2}>
                <Text fz={13} fw={700} style={{ color: 'inherit' }}>
                  {t('weekReportCorrection.bannerPreviousReasonLabel')}
                </Text>
                <Text fz={13} style={{ color: 'inherit' }}>
                  „{report.rejectionReason}“
                </Text>
              </Stack>
            </Stack>
            <CloseButton
              size={20}
              onClick={() => setBannerDismissed(true)}
              style={{ color: 'inherit' }}
            />
          </Group>
        )}

        <CardSection title={t('newWeekReport.basicInfoTitle')}>
          <Box p={20} style={{ display: 'flex', flexWrap: 'wrap', gap: 16, width: '100%' }}>
            <div style={{ flex: '1 1 220px', minWidth: 220 }}>
              <SelectField
                label={t('newWeekReport.contractLabel')}
                data={[]}
                value={contract?.id ?? null}
                disabled
                placeholder={
                  contract
                    ? `${contract.contractNumber ?? ''} — ${otherPartyName(contract, user?.orgName)}`
                    : '—'
                }
              />
            </div>
            <div style={{ flex: '1 1 220px', minWidth: 220 }}>
              <SelectField
                label={t('newWeekReport.weekLabel')}
                data={[]}
                value={null}
                disabled
                placeholder={formatWeekLabel(report.weekNumber, report.reportYear)}
              />
            </div>
            <div style={{ flex: '1 1 220px', minWidth: 220 }}>
              <SelectField
                label={t('newWeekReport.teamLabel')}
                data={[]}
                value={null}
                disabled
                placeholder={teamName ?? '—'}
              />
            </div>
          </Box>
        </CardSection>

        <CardSection title={t('newWeekReport.itemsTitle')}>
          {lines.length > 0 && (
            <Box style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 890 }}>
                <div
                  className="table-header-row"
                  style={{
                    ...tableRowStyle,
                    padding: '12px 20px',
                    backgroundColor: 'var(--color-bg-canvas)',
                    borderBottom: '1px solid var(--color-border-soft)'
                  }}
                >
                  {itemColumns.map(([key, width]) => (
                    <Text
                      key={key}
                      fz={10}
                      fw={600}
                      w={width}
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                    >
                      {t(key)}
                    </Text>
                  ))}
                </div>

                {lines.map((line, index) => {
                  const plannedItem = line.plannedItemId
                    ? plannedItemMap.get(line.plannedItemId)
                    : undefined;
                  const plannedQty = plannedItem?.plannedQty;
                  const behindPlan =
                    plannedQty !== undefined &&
                    line.realizedQty !== undefined &&
                    line.realizedQty < plannedQty;

                  return (
                    <div
                      key={line.id}
                      style={{
                        ...tableRowStyle,
                        padding: '14px 20px',
                        borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                      }}
                    >
                      <Text
                        fz={13.5}
                        fw={600}
                        w={210}
                        style={{ color: 'var(--color-text-primary)' }}
                      >
                        {index + 1}. {line.description}
                      </Text>
                      <Text
                        fz={11}
                        fw={600}
                        px={10}
                        py={2}
                        w={130}
                        style={{
                          borderRadius: 999,
                          backgroundColor: line.isAdditionalWork
                            ? 'var(--color-accent-primary-soft)'
                            : 'var(--color-bg-surface-2)',
                          color: line.isAdditionalWork
                            ? 'var(--color-accent-primary-active)'
                            : 'var(--color-text-muted)',
                          width: 'fit-content'
                        }}
                      >
                        {line.isAdditionalWork
                          ? t('newWeekReport.sourceAdditional')
                          : t('newWeekReport.sourcePlan')}
                      </Text>
                      <div style={{ width: 130 }}>
                        {behindPlan ? (
                          <TextInput
                            value={String(line.realizedQty ?? '')}
                            readOnly
                            w={90}
                            styles={{
                              input: {
                                borderWidth: 1.5,
                                borderColor: 'var(--color-status-warning)',
                                backgroundColor: 'var(--color-bg-surface)',
                                color: 'var(--color-text-primary)',
                                fontWeight: 600,
                                height: 32,
                                minHeight: 32
                              }
                            }}
                          />
                        ) : (
                          <Text fz={13.5} style={{ color: 'var(--color-text-primary)' }}>
                            {line.realizedQty ?? '—'}
                          </Text>
                        )}
                      </div>
                      <Text fz={13.5} w={80} style={{ color: 'var(--color-text-secondary)' }}>
                        {line.unit ?? '—'}
                      </Text>
                      <Group gap={6} wrap="nowrap" w={150}>
                        <IconFigmaLock size={12} color="var(--color-text-secondary)" />
                        <Text fz={13.5} style={{ color: 'var(--color-text-secondary)' }}>
                          {(line.snapshotPrice ?? 0).toFixed(2)} €/{line.unit}
                        </Text>
                      </Group>
                      <div style={{ width: 190 }}>
                        {behindPlan ? (
                          <Stack gap={4}>
                            <Textarea
                              readOnly
                              placeholder={t('weekReportCorrection.noteWarningPlaceholder')}
                              minRows={2}
                              styles={{
                                input: {
                                  borderWidth: 1.5,
                                  borderColor: 'var(--color-status-warning)',
                                  backgroundColor: 'var(--color-bg-surface)',
                                  fontSize: 12.5
                                }
                              }}
                            />
                            <Text fz={10.5} style={{ color: 'var(--color-status-warning)' }}>
                              {t('weekReportCorrection.noteWarningHelper')}
                            </Text>
                          </Stack>
                        ) : (
                          <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                            —
                          </Text>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Box>
          )}

          {lines.length === 0 && (
            <Center py={30}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('newWeekReport.emptyItems')}
              </Text>
            </Center>
          )}

          <Group gap={12} p={16} style={{ borderTop: '1px solid var(--color-border-soft)' }}>
            <Button variant="outline" size="xs" disabled>
              {t('newWeekReport.addFromPlan')}
            </Button>
            <Button variant="outline" size="xs" disabled>
              {t('newWeekReport.addAdditional')}
            </Button>
          </Group>
        </CardSection>

        <CardSection title={t('weekReportCorrection.hoursTitle')}>
          <Stack gap={16} p={20}>
            {hours.map((hour) => (
              <Group key={hour.id} gap={16} align="flex-end" wrap="wrap">
                <div style={{ flex: '1 1 320px', minWidth: 220 }}>
                  <SelectField
                    label={t('weekReportCorrection.hoursWorkerLabel')}
                    data={[]}
                    value={null}
                    disabled
                    placeholder={hour.userId ? (userMap.get(hour.userId) ?? hour.userId) : '—'}
                  />
                </div>
                <div style={{ width: 116 }}>
                  <SelectField
                    label={t('weekReportCorrection.hoursHoursLabel')}
                    data={[]}
                    value={null}
                    disabled
                    placeholder={String(hour.hours ?? '—')}
                  />
                </div>
              </Group>
            ))}

            {hours.length === 0 && (
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('newWeekReport.emptyItems')}
              </Text>
            )}

            <Box>
              <Button variant="outline" size="xs" disabled>
                {t('weekReportCorrection.hoursAddWorker')}
              </Button>
            </Box>
          </Stack>
        </CardSection>

        <CardSection title={t('weekReportCorrection.attachmentsTitle')}>
          <Stack gap={12} p={20}>
            {attachments.map((attachment) => (
              <Group
                key={attachment.id}
                justify="space-between"
                wrap="nowrap"
                p={16}
                style={{
                  width: '100%',
                  borderRadius: 6,
                  border: '1px solid var(--color-border-soft)',
                  backgroundColor: 'var(--color-bg-canvas)'
                }}
              >
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text fz={13} fw={600} style={{ color: 'var(--color-text-primary)' }} truncate>
                    {attachment.fileName}
                  </Text>
                  <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                    {attachment.fileSizeKb ? `${(attachment.fileSizeKb / 1024).toFixed(1)} MB` : ''}
                  </Text>
                </Stack>
                <Group gap={16} wrap="nowrap">
                  <Switch
                    label={t('weekReportCorrection.attachmentsVisibleToClient')}
                    labelPosition="left"
                    checked={!!attachment.isVisibleToClient}
                    disabled
                    readOnly
                    styles={{ label: { fontSize: 13, color: 'var(--color-text-secondary)' } }}
                  />
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    loading={
                      deleteAttachmentMutation.isPending &&
                      deleteAttachmentMutation.variables?.attachmentId === attachment.id
                    }
                    onClick={() =>
                      id &&
                      attachment.id &&
                      deleteAttachmentMutation.mutate({ id, attachmentId: attachment.id })
                    }
                  >
                    <IconFigmaTrash size={16} color="var(--color-text-secondary)" />
                  </ActionIcon>
                </Group>
              </Group>
            ))}

            {attachments.length === 0 && (
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('newWeekReport.emptyItems')}
              </Text>
            )}

            <Group gap={16} wrap="wrap">
              <MutationButton
                variant="outline"
                size="xs"
                isPending={uploadAttachmentMutation.isPending}
                isPaused={uploadAttachmentMutation.isPaused}
                onClick={() => fileInputRef.current?.click()}
              >
                {t('weekReportCorrection.attachmentsAdd')}
              </MutationButton>
              <Switch
                label={t('weekReportCorrection.attachmentsVisibleToClient')}
                labelPosition="left"
                checked={uploadVisibleToClient}
                onChange={(event) => setUploadVisibleToClient(event.currentTarget.checked)}
                styles={{ label: { fontSize: 13, color: 'var(--color-text-secondary)' } }}
              />
              <input ref={fileInputRef} type="file" hidden onChange={handleFileSelected} />
            </Group>
          </Stack>
        </CardSection>

        <FormActions>
          <Button variant="outline" onClick={() => navigate(`/weekly-reports/${id}`)}>
            {t('newWeekReport.saveDraft')}
          </Button>
          <MutationButton
            isPending={submitMutation.isPending}
            isPaused={submitMutation.isPaused}
            onClick={() => id && submitMutation.mutate({ id })}
            rightSection={<IconFigmaArrowRight size={18} />}
          >
            {t('weekReportCorrection.resubmit')}
          </MutationButton>
        </FormActions>
      </Stack>

      <SubmitSuccessModal
        opened={submitSuccessOpen}
        variant="correction"
        weekNumber={report.weekNumber ?? 0}
        period={formatWeekPeriod(report.weekNumber, report.reportYear)}
        recipient={contract ? otherPartyName(contract, user?.orgName) : '—'}
        onClose={() => {
          setSubmitSuccessOpen(false);
          navigate('/weekly-reports');
        }}
      />
    </Container>
  );
};
